import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Building2, Users, CheckCircle, XCircle, RefreshCw, Loader2, AlertCircle, MessageSquare, Package } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface Empresa {
  id: string;
  tipo_documento: string;
  codigo_fiscal: string;
  nombre: string;
  tipo_personalidad: string;
  direccion: string;
  localidad: string;
  codigo_postal: string;
  pais: string;
  email: string;
  telefono: string;
  persona_contacto: string;
  email_contacto: string;
  telefono_contacto: string;
  admin_id: string;
  mensajes_max: number;
  tipo_suscripcion: string;
  tipo_suscripcion_id?: string;
  estado: string;
  created_at: string;
  updated_at: string;
  admin?: {
    nombre: string;
    apellidos: string;
    email: string;
  };
  usuarios_count?: number;
  tipo?: {
    nombre: string;
    codigo: string;
    mensajes_max: number;
  };
}

interface User {
  id: string;
  email: string;
  nombre: string;
  apellidos: string;
  role: string;
  estado: string;
  empresa_id?: string;
}

interface TipoSuscripcion {
  id: string;
  codigo: string;
  nombre: string;
  mensajes_max: number;
  precio: number;
  estado: string;
}

export default function Empresas() {
  const { user } = useAuthStore();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [tiposSuscripcion, setTiposSuscripcion] = useState<TipoSuscripcion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState<Empresa | null>(null);
  const [empresaUsuarios, setEmpresaUsuarios] = useState<User[]>([]);
  const [availableUsuarios, setAvailableUsuarios] = useState<User[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newEmpresa, setNewEmpresa] = useState({
    tipo_documento: 'CIF',
    codigo_fiscal: '',
    nombre: '',
    tipo_personalidad: 'jurídica',
    direccion: '',
    localidad: '',
    codigo_postal: '',
    pais: 'España',
    email: '',
    telefono: '',
    persona_contacto: '',
    email_contacto: '',
    telefono_contacto: '',
    admin_id: '',
    mensajes_max: 1000,
    tipo_suscripcion_id: '',
    estado: 'activo'
  });

  useEffect(() => {
    if (user) {
      fetchEmpresas();
      fetchUsuarios();
      fetchTiposSuscripcion();
    }
  }, [user]);

  const fetchEmpresas = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Get all empresas with their admin info - specify the relationship explicitly
      const { data: empresasData, error: empresasError } = await supabase
        .from('empresas')
        .select(`
          *,
          admin:usuarios!empresas_admin_id_fkey(nombre, apellidos, email),
          tipo:tipos_suscripcion(nombre, codigo, mensajes_max)
        `)
        .order('created_at', { ascending: false });

      if (empresasError) throw empresasError;

      // Get count of users for each empresa
      const empresasWithCounts = await Promise.all((empresasData || []).map(async (empresa) => {
        const { count, error: countError } = await supabase
          .from('usuarios')
          .select('*', { count: 'exact', head: true })
          .eq('empresa_id', empresa.id);
        
        if (countError) {
          console.error('Error getting user count:', countError);
          return { ...empresa, usuarios_count: 0 };
        }
        
        return { ...empresa, usuarios_count: count || 0 };
      }));

      setEmpresas(empresasWithCounts || []);
    } catch (error: any) {
      console.error('Error fetching empresas:', error);
      setError('Error al cargar las empresas: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const { data: usuariosData, error: usuariosError } = await supabase
        .from('usuarios')
        .select('*')
        .eq('estado', 'activo')
        .order('nombre');

      if (usuariosError) throw usuariosError;
      setUsuarios(usuariosData || []);
    } catch (error: any) {
      console.error('Error fetching usuarios:', error);
      setError('Error al cargar los usuarios');
    }
  };

  const fetchTiposSuscripcion = async () => {
    try {
      const { data, error } = await supabase
        .from('tipos_suscripcion')
        .select('id, codigo, nombre, mensajes_max, precio, estado')
        .eq('estado', 'activo')
        .order('precio');

      if (error) throw error;
      setTiposSuscripcion(data || []);
    } catch (error: any) {
      console.error('Error fetching subscription types:', error);
      setError('Error al cargar los tipos de suscripción');
    }
  };

  const fetchEmpresaUsuarios = async (empresaId: string) => {
    try {
      // Get users associated with the empresa
      const { data: empresaUsuariosData, error: empresaUsuariosError } = await supabase
        .from('usuarios')
        .select('*')
        .eq('empresa_id', empresaId);

      if (empresaUsuariosError) throw empresaUsuariosError;
      setEmpresaUsuarios(empresaUsuariosData || []);

      // Get users not associated with any empresa or with a different empresa
      const { data: availableUsersData, error: availableUsersError } = await supabase
        .from('usuarios')
        .select('*')
        .is('empresa_id', null)
        .eq('estado', 'activo');

      if (availableUsersError) throw availableUsersError;
      setAvailableUsuarios(availableUsersData || []);
    } catch (error: any) {
      console.error('Error fetching empresa usuarios:', error);
      setError('Error al cargar los usuarios de la empresa');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (selectedEmpresa) {
        // Update existing empresa
        const { error } = await supabase
          .from('empresas')
          .update({
            tipo_documento: newEmpresa.tipo_documento,
            codigo_fiscal: newEmpresa.codigo_fiscal,
            nombre: newEmpresa.nombre,
            tipo_personalidad: newEmpresa.tipo_personalidad,
            direccion: newEmpresa.direccion,
            localidad: newEmpresa.localidad,
            codigo_postal: newEmpresa.codigo_postal,
            pais: newEmpresa.pais,
            email: newEmpresa.email,
            telefono: newEmpresa.telefono,
            persona_contacto: newEmpresa.persona_contacto,
            email_contacto: newEmpresa.email_contacto,
            telefono_contacto: newEmpresa.telefono_contacto,
            admin_id: newEmpresa.admin_id,
            mensajes_max: newEmpresa.mensajes_max,
            tipo_suscripcion_id: newEmpresa.tipo_suscripcion_id,
            estado: newEmpresa.estado
          })
          .eq('id', selectedEmpresa.id);

        if (error) throw error;
        setSuccess('Empresa actualizada correctamente');
      } else {
        // Create new empresa
        const { error } = await supabase
          .from('empresas')
          .insert([{
            tipo_documento: newEmpresa.tipo_documento,
            codigo_fiscal: newEmpresa.codigo_fiscal,
            nombre: newEmpresa.nombre,
            tipo_personalidad: newEmpresa.tipo_personalidad,
            direccion: newEmpresa.direccion,
            localidad: newEmpresa.localidad,
            codigo_postal: newEmpresa.codigo_postal,
            pais: newEmpresa.pais,
            email: newEmpresa.email,
            telefono: newEmpresa.telefono,
            persona_contacto: newEmpresa.persona_contacto,
            email_contacto: newEmpresa.email_contacto,
            telefono_contacto: newEmpresa.telefono_contacto,
            admin_id: newEmpresa.admin_id || user?.id,
            mensajes_max: newEmpresa.mensajes_max,
            tipo_suscripcion_id: newEmpresa.tipo_suscripcion_id,
            estado: newEmpresa.estado
          }]);

        if (error) throw error;
        setSuccess('Empresa creada correctamente');
      }

      setShowModal(false);
      setSelectedEmpresa(null);
      resetForm();
      fetchEmpresas();
    } catch (error: any) {
      console.error('Error saving empresa:', error);
      setError(error.message);
    }
  };

  const handleEdit = (empresa: Empresa) => {
    setSelectedEmpresa(empresa);
    setNewEmpresa({
      tipo_documento: empresa.tipo_documento,
      codigo_fiscal: empresa.codigo_fiscal,
      nombre: empresa.nombre,
      tipo_personalidad: empresa.tipo_personalidad,
      direccion: empresa.direccion || '',
      localidad: empresa.localidad || '',
      codigo_postal: empresa.codigo_postal || '',
      pais: empresa.pais || 'España',
      email: empresa.email || '',
      telefono: empresa.telefono || '',
      persona_contacto: empresa.persona_contacto || '',
      email_contacto: empresa.email_contacto || '',
      telefono_contacto: empresa.telefono_contacto || '',
      admin_id: empresa.admin_id || '',
      mensajes_max: empresa.mensajes_max || 1000,
      tipo_suscripcion_id: empresa.tipo_suscripcion_id || '',
      estado: empresa.estado || 'activo'
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta empresa? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      // First, remove empresa_id from all associated users
      const { error: updateError } = await supabase
        .from('usuarios')
        .update({ empresa_id: null })
        .eq('empresa_id', id);

      if (updateError) throw updateError;

      // Then delete the empresa
      const { error } = await supabase
        .from('empresas')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Empresa eliminada correctamente');
      fetchEmpresas();
    } catch (error: any) {
      console.error('Error deleting empresa:', error);
      setError(error.message);
    }
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('empresas')
        .update({ estado: newStatus })
        .eq('id', id);

      if (error) throw error;
      setSuccess(`Empresa ${newStatus === 'activo' ? 'activada' : 'desactivada'} correctamente`);
      fetchEmpresas();
    } catch (error: any) {
      console.error('Error updating empresa status:', error);
      setError(error.message);
    }
  };

  const handleManageUsers = (empresa: Empresa) => {
    setSelectedEmpresa(empresa);
    fetchEmpresaUsuarios(empresa.id);
    setShowUsersModal(true);
  };

  const handleAddUserToEmpresa = async (userId: string) => {
    if (!selectedEmpresa) return;

    try {
      // Update the user's empresa_id
      const { error } = await supabase
        .from('usuarios')
        .update({ empresa_id: selectedEmpresa.id })
        .eq('id', userId);

      if (error) throw error;
      setSuccess('Usuario añadido a la empresa correctamente');
      fetchEmpresaUsuarios(selectedEmpresa.id);
    } catch (error: any) {
      console.error('Error adding user to empresa:', error);
      setError(error.message);
    }
  };

  const handleRemoveUserFromEmpresa = async (userId: string) => {
    if (!selectedEmpresa) return;

    try {
      // Remove the empresa_id from the user
      const { error } = await supabase
        .from('usuarios')
        .update({ empresa_id: null })
        .eq('id', userId)
        .eq('empresa_id', selectedEmpresa.id);

      if (error) throw error;
      setSuccess('Usuario eliminado de la empresa correctamente');
      fetchEmpresaUsuarios(selectedEmpresa.id);
    } catch (error: any) {
      console.error('Error removing user from empresa:', error);
      setError(error.message);
    }
  };

  const resetForm = () => {
    setNewEmpresa({
      tipo_documento: 'CIF',
      codigo_fiscal: '',
      nombre: '',
      tipo_personalidad: 'jurídica',
      direccion: '',
      localidad: '',
      codigo_postal: '',
      pais: 'España',
      email: '',
      telefono: '',
      persona_contacto: '',
      email_contacto: '',
      telefono_contacto: '',
      admin_id: '',
      mensajes_max: 1000,
      tipo_suscripcion_id: tiposSuscripcion.length > 0 ? tiposSuscripcion[0].id : '',
      estado: 'activo'
    });
  };

  const filteredEmpresas = empresas.filter(empresa => 
    empresa.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    empresa.codigo_fiscal.toLowerCase().includes(searchTerm.toLowerCase()) ||
    empresa.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    empresa.localidad?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Empresas</h1>
        <button 
          onClick={() => {
            setSelectedEmpresa(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Empresa
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md mb-4">
        <div className="p-4 border-b flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[250px]">
            <input
              type="text"
              placeholder="Buscar por nombre, email o usuario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          
          <button
            onClick={fetchEmpresas}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            title="Actualizar lista"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left bg-gray-50">
                <th className="px-4 py-3 font-semibold text-gray-600">Nombre</th>
                <th className="px-4 py-3 font-semibold text-gray-600">CIF/NIF</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Tipo</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Localidad</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Contacto</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Administrador</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Usuarios</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Suscripción</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Mensajes</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-3 font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEmpresas.map((empresa) => (
                <tr key={empresa.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Building2 className="w-5 h-5 text-blue-500 mr-2" />
                      <span className="font-medium">{empresa.nombre}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <span className="text-sm">{empresa.tipo_documento}: {empresa.codigo_fiscal}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      empresa.tipo_personalidad === 'jurídica' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {empresa.tipo_personalidad}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {empresa.localidad && (
                      <span className="text-sm">{empresa.localidad}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      {empresa.persona_contacto && (
                        <div>{empresa.persona_contacto}</div>
                      )}
                      {empresa.email && (
                        <div className="text-gray-500">{empresa.email}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {empresa.admin && (
                      <div className="text-sm">
                        <div>{empresa.admin.nombre} {empresa.admin.apellidos}</div>
                        <div className="text-gray-500">{empresa.admin.email}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleManageUsers(empresa)}
                      className="flex items-center text-blue-500 hover:text-blue-600"
                    >
                      <Users className="w-4 h-4 mr-1" />
                      <span>{empresa.usuarios_count}</span>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Package className="w-4 h-4 text-gray-500 mr-1 flex-shrink-0" />
                      <span className="text-sm">{empresa.tipo?.nombre || empresa.tipo_suscripcion}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <MessageSquare className="w-4 h-4 mr-1 text-gray-500" />
                      <span className="text-sm font-medium">
                        {empresa.mensajes_max === 0 ? 'Ilimitados' : empresa.mensajes_max}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      empresa.estado === 'activo' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {empresa.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-1">
                      <button 
                        onClick={() => handleUpdateStatus(empresa.id, empresa.estado === 'activo' ? 'inactivo' : 'activo')}
                        className={`p-1 rounded-full ${
                          empresa.estado === 'activo' ? 'text-red-500 hover:bg-red-100' : 'text-green-500 hover:bg-green-100'
                        }`}
                        title={empresa.estado === 'activo' ? 'Desactivar empresa' : 'Activar empresa'}
                      >
                        {empresa.estado === 'activo' ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                      </button>
                      <button 
                        onClick={() => handleEdit(empresa)}
                        className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                        title="Editar empresa"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button 
                        onClick={() => handleDelete(empresa.id)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                        title="Eliminar empresa"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredEmpresas.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No se encontraron empresas</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t bg-gray-50 text-sm text-gray-500">
          Mostrando {filteredEmpresas.length} de {empresas.length} empresas
        </div>
      </div>

      {/* Modal para añadir/editar empresa */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">
                {selectedEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
              </h2>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Documento
                    </label>
                    <select
                      value={newEmpresa.tipo_documento}
                      onChange={(e) => setNewEmpresa({...newEmpresa, tipo_documento: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="CIF">CIF</option>
                      <option value="DNI">DNI</option>
                      <option value="NIE">NIE</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código de Identificación Fiscal
                    </label>
                    <input
                      type="text"
                      required
                      value={newEmpresa.codigo_fiscal}
                      onChange={(e) => setNewEmpresa({...newEmpresa, codigo_fiscal: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="B12345678"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de la Empresa
                  </label>
                  <input
                    type="text"
                    required
                    value={newEmpresa.nombre}
                    onChange={(e) => setNewEmpresa({...newEmpresa, nombre: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Nombre de la empresa"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Personalidad
                    </label>
                    <select
                      value={newEmpresa.tipo_personalidad}
                      onChange={(e) => setNewEmpresa({...newEmpresa, tipo_personalidad: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="jurídica">Jurídica</option>
                      <option value="física">Física</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Administrador
                    </label>
                    <select
                      value={newEmpresa.admin_id}
                      onChange={(e) => setNewEmpresa({...newEmpresa, admin_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Seleccionar administrador</option>
                      {usuarios
                        .filter(u => u.role === 'administrador')
                        .map((usuario) => (
                          <option key={usuario.id} value={usuario.id}>
                            {usuario.nombre} {usuario.apellidos} ({usuario.email})
                          </option>
                        ))
                      }
                    </select>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección
                  </label>
                  <input
                    type="text"
                    value={newEmpresa.direccion}
                    onChange={(e) => setNewEmpresa({...newEmpresa, direccion: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Dirección completa"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Localidad
                    </label>
                    <input
                      type="text"
                      value={newEmpresa.localidad}
                      onChange={(e) => setNewEmpresa({...newEmpresa, localidad: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Ciudad o municipio"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código Postal
                    </label>
                    <input
                      type="text"
                      value={newEmpresa.codigo_postal}
                      onChange={(e) => setNewEmpresa({...newEmpresa, codigo_postal: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="28001"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      País
                    </label>
                    <input
                      type="text"
                      value={newEmpresa.pais}
                      onChange={(e) => setNewEmpresa({...newEmpresa, pais: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="España"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newEmpresa.email}
                      onChange={(e) => setNewEmpresa({...newEmpresa, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="info@empresa.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono
                    </label>
                    <input
                      type="text"
                      value={newEmpresa.telefono}
                      onChange={(e) => setNewEmpresa({...newEmpresa, telefono: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="912345678"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <h3 className="font-medium text-gray-700 mb-2 border-b pb-1">Información de Contacto</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Persona de Contacto
                    </label>
                    <input
                      type="text"
                      value={newEmpresa.persona_contacto}
                      onChange={(e) => setNewEmpresa({...newEmpresa, persona_contacto: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Nombre y apellidos"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email de Contacto
                    </label>
                    <input
                      type="email"
                      value={newEmpresa.email_contacto}
                      onChange={(e) => setNewEmpresa({...newEmpresa, email_contacto: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="contacto@empresa.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Teléfono de Contacto
                    </label>
                    <input
                      type="text"
                      value={newEmpresa.telefono_contacto}
                      onChange={(e) => setNewEmpresa({...newEmpresa, telefono_contacto: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="912345678"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <h3 className="font-medium text-gray-700 mb-2 border-b pb-1">Configuración de Suscripción</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Suscripción
                    </label>
                    <select
                      value={newEmpresa.tipo_suscripcion_id}
                      onChange={(e) => {
                        const selectedTipo = tiposSuscripcion.find(t => t.id === e.target.value);
                        setNewEmpresa({
                          ...newEmpresa, 
                          tipo_suscripcion_id: e.target.value,
                          mensajes_max: selectedTipo ? selectedTipo.mensajes_max : newEmpresa.mensajes_max
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Seleccionar tipo de suscripción</option>
                      {tiposSuscripcion.map(tipo => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.nombre} - {tipo.mensajes_max === 0 ? 'Mensajes ilimitados' : `${tipo.mensajes_max} mensajes`} - {tipo.precio.toFixed(2)}€
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mensajes Máximos al Mes
                    </label>
                    <input
                      type="number"
                      value={newEmpresa.mensajes_max}
                      onChange={(e) => setNewEmpresa({...newEmpresa, mensajes_max: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      min="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Establece 0 para mensajes ilimitados
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    value={newEmpresa.estado}
                    onChange={(e) => setNewEmpresa({...newEmpresa, estado: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </form>
            </div>

            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedEmpresa(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {selectedEmpresa ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para gestionar usuarios de la empresa */}
      {showUsersModal && selectedEmpresa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Usuarios en {selectedEmpresa.nombre}
              </h2>
              <button
                onClick={() => {
                  setShowUsersModal(false);
                  setSelectedEmpresa(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Lista de usuarios en la empresa */}
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Usuarios en la empresa</h3>
                <div className="border rounded-lg divide-y">
                  {empresaUsuarios.map(user => (
                    <div key={user.id} className="p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{user.nombre} {user.apellidos}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveUserFromEmpresa(user.id)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                        title="Eliminar de la empresa"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  {empresaUsuarios.length === 0 && (
                    <p className="p-3 text-gray-500 text-center">No hay usuarios en esta empresa</p>
                  )}
                </div>
              </div>

              {/* Lista de usuarios disponibles */}
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Usuarios disponibles</h3>
                <div className="border rounded-lg divide-y">
                  {availableUsuarios.map(user => (
                    <div key={user.id} className="p-3 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{user.nombre} {user.apellidos}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.role === 'administrador' 
                            ? 'bg-purple-100 text-purple-800' 
                            : user.role === 'usuario_avanzado'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                      </div>
                      <button
                        onClick={() => handleAddUserToEmpresa(user.id)}
                        className="p-1 text-green-500 hover:bg-green-100 rounded-full"
                        title="Añadir a la empresa"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  {availableUsuarios.length === 0 && (
                    <p className="p-3 text-gray-500 text-center">No hay usuarios disponibles</p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowUsersModal(false);
                  setSelectedEmpresa(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}